import { Device } from '@twilio/voice-sdk';
import { phoneModes } from '../constants/phone.constants';
import { useContactStore } from '../features/dialer/store/contact.store';
import { useDeviceStore } from '../store/device.store';
import { getProvider } from '@/lib/callProviders/provider-registry';
import { useDialerCallPulseStore } from '../store/dialer-call-pulse.store';
import { useDialerStore } from '../store/dialer.store';
import { usePhoneStore } from '../store/phone.store';
import { useWaitingCallStore } from '../store/waiting-call.store';
import { hasWaitingQueuedCalls } from '../features/queued-calls/queued-calls.helpers';

export const usePhone = () => {
  const phoneStore = usePhoneStore();
  const dialerStore = useDialerStore();
  const contactStore = useContactStore();
  const deviceStore = useDeviceStore();
  const dialerCallPulseStore = useDialerCallPulseStore();

  const teardownPhone = (resetPhone: VoidFunction) => {
    const call = deviceStore?.call;
    const provider = call ? getProvider(call.provider) : undefined;
    if (call) {
      provider?.disconnect(call);
      provider?.ignore(call);
    }

    // closing the phone must also tear down a still-ringing waiting call, otherwise a stale
    // waitingCall would reappear as the bar the next time the phone opens.
    const { waitingCall, resetWaitingCall } = useWaitingCallStore.getState();
    waitingCall?.reject();
    resetWaitingCall();
    // `Silent` on the waiting-call bar disables the ringtone on the *device*, not on that one
    // call, so every other path that clears waitingCall restores it. Without this, silencing a
    // second call and then closing the phone leaves the device permanently unable to ring for
    // any later incoming call.
    (deviceStore?.device?.providerDevice as Device | undefined)?.audio?.incoming(true);

    deviceStore?.updateAcceptedTimestamp('');
    resetPhone();
    dialerStore.resetDialer();
    contactStore.resetContact();
    dialerCallPulseStore.resetDialerCallPulse();
    deviceStore.resetCall();
  };

  const closePhone = () => teardownPhone(phoneStore.resetPhone);

  const closePhoneAfterCall = () =>
    teardownPhone(hasWaitingQueuedCalls() ? phoneStore.resetPhoneKeepOpen : phoneStore.resetPhone);

  const handleTabSelect = (openTab: VoidFunction, shouldOpenInCallView?: boolean) => {
    openTab();
    contactStore.resetContact();
    if (phoneStore.mode !== phoneModes.dialer) phoneStore.openDialer();

    // when there is no active call or the clicked tab is not dialer, do nothing
    if (!deviceStore.call || !shouldOpenInCallView) return;

    if (phoneStore.isIncoming) phoneStore.openIncomingPhone(false);
    else phoneStore.openPhoneInCallMode();
  };

  const collapsePhone = () => {};

  return { closePhone, closePhoneAfterCall, handleTabSelect, collapsePhone };
};
